import * as React from "react";
import { useFormik } from "formik";
import { useHistory, Link, useParams } from "react-router-dom";
import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { toast } from "react-toastify";

// Components
import InputText from "../InputText/InputText";
import Loader from "../Loader/Loader";
import ChangeLanguage from "../ChangeLanguage/ChangeLanguage";

// Utils
import { RESET_USER_PASSWORD } from "../../queries/User.queries";
import useLanguageString from "../../hooks/LanguageStrings.hooks";

// Styles
import LoginIllustration from "../../static/images/login-illustration.svg";
import SonnenLogo from "../../static/images/logo-sonnen-white.svg";
import "./ResetPassword.scss";

const MUTATION_RESET_PASSWORD = gql`
  ${RESET_USER_PASSWORD}
`;

const ResetPassword: React.FC = () => {
  const history = useHistory();
  const params: any = useParams();
  const [strings] = useLanguageString(params.countryCode, "passwordNew");

  const [resetPassword] = useMutation(MUTATION_RESET_PASSWORD);
  const [resetKey, setResetKey] = React.useState<string | null>(null);
  const [userLogin, setUserLogin] = React.useState<string | null>(null);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has("key") && urlParams.has("user")) {
      setResetKey(urlParams.get("key"));
      setUserLogin(urlParams.get("user"));
    }
  }, [setResetKey]);

  const formik = useFormik({
    initialValues: {
      password: "",
      passwordCheck: "",
    },
    onSubmit: (values) => {
      const { password, passwordCheck } = values;

      if (password === passwordCheck) {
        resetPassword({
          variables: {
            key: resetKey,
            login: userLogin,
            password: values.password,
          },
        })
          .then(() => {
            toast.success(strings?.passwordNew.stringSuccess, {
              onClose: () => history.push(`/${params.countryCode}/login`),
            });
          })
          .catch(() => {
            toast.error(strings?.passwordNew.stringError);
          });
      } else {
        toast.error(strings?.passwordNew.stringErrorPassword, {
          autoClose: 5000,
        });
      }
    },
  });

  return (
    <div className="resetPassword">
      <div className="resetPassword-container">
        <div className="resetPassword-container__inner">
          <div className="resetPassword-container__header">
            <img src={SonnenLogo} alt="sonnen" />
          </div>
          {!strings && <Loader />}
          {strings && (
            <>
              <div className="resetPassword-container__intro">
                <img src={LoginIllustration} alt="resetPassword" />
                <h1>{strings.passwordNew.stringHeadline}</h1>
              </div>
              <form onSubmit={formik.handleSubmit}>
                <InputText
                  id="password"
                  type="password"
                  label={strings.passwordNew.stringPassword}
                  minlength={6}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  required
                />
                <InputText
                  id="passwordCheck"
                  type="password"
                  className="mt-3"
                  label={strings.passwordNew.stringPasswordRepeat}
                  minlength={6}
                  value={formik.values.passwordCheck}
                  onChange={formik.handleChange}
                  required
                />
                <button type="submit" className="mt-4">
                  {strings.passwordNew.stringButton}
                </button>
              </form>
              <p className="resetPassword-container__register text-muted">
                {`${strings.passwordNew.stringBack} `}{" "}
                <Link to={`/${params.countryCode}/login`}>
                  {strings.passwordNew.stringBackTitle}
                </Link>
              </p>
              <ChangeLanguage />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
